import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Info } from 'lucide-react';

export type PolicySeverity = 'info' | 'warning' | 'critical';
export type PolicyEnforcement = 'warn' | 'block';
export type MonitoringFrequency = 'day' | 'week';

export interface PolicyCheckConfig {
  enabled: boolean;
  threshold: number;
  severity: PolicySeverity;
  enforcement: PolicyEnforcement;
}

export interface PolicyCheckCardProps {
  title: string;
  config: PolicyCheckConfig;
  onChange: (config: PolicyCheckConfig) => void;
  monitoringFrequency: MonitoringFrequency;
  expectedHoursPerWeek: number;
  dailyHours?: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  isMissingHours?: boolean;
  isNonWorkingDay?: boolean;
  isLegalMaximum?: boolean;
}

const getSeverityBadgeVariant = (severity: PolicySeverity) => {
  switch (severity) {
    case 'info':
      return 'default';
    case 'warning':
      return 'secondary';
    case 'critical':
      return 'destructive';
    default:
      return 'default';
  }
};

const getSeverityLabel = (severity: PolicySeverity) => {
  switch (severity) {
    case 'info':
      return 'Info';
    case 'warning':
      return 'Warning';
    case 'critical':
      return 'Critical';
    default:
      return severity;
  }
};

const getEnforcementLabel = (enforcement: PolicyEnforcement) => {
  switch (enforcement) {
    case 'warn':
      return 'Warn only';
    case 'block':
      return 'Block submission';
    default:
      return enforcement;
  }
};

export const PolicyCheckCard: React.FC<PolicyCheckCardProps> = ({
  title,
  config,
  onChange,
  monitoringFrequency,
  expectedHoursPerWeek,
  dailyHours,
  isMissingHours = false,
  isNonWorkingDay = false,
  isLegalMaximum = false,
}) => {
  // Local state for threshold input to allow editing
  const [thresholdInput, setThresholdInput] = React.useState<string>(config.threshold.toString());

  // Update local input when config changes externally
  React.useEffect(() => {
    setThresholdInput(config.threshold.toString());
  }, [config.threshold]);

  const handleToggle = (checked: boolean) => {
    onChange({ ...config, enabled: checked });
  };

  const handleThresholdChange = (value: string) => {
    setThresholdInput(value);
    
    // Only update parent if it's a valid number
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue >= 0) {
      onChange({ ...config, threshold: numValue });
    }
  };

  const handleThresholdBlur = () => {
    // On blur, ensure we have a valid value
    const numValue = parseFloat(thresholdInput);
    if (isNaN(numValue) || numValue < 0) {
      // Reset to previous valid value
      setThresholdInput(config.threshold.toString());
    }
  };

  const handleSeverityChange = (value: PolicySeverity) => {
    onChange({ ...config, severity: value });
  };

  const handleEnforcementChange = (value: PolicyEnforcement) => {
    onChange({ ...config, enforcement: value });
  };

  // Calculate alert threshold for weekly monitoring
  const weeklyAlertThreshold = isMissingHours 
    ? expectedHoursPerWeek - config.threshold 
    : expectedHoursPerWeek + config.threshold;

  // Calculate average daily hours for weekly monitoring
  const avgDailyHours = expectedHoursPerWeek / 5; // Assuming 5-day work week

  // Get daily breakdown for daily monitoring
  const getDailyBreakdown = () => {
    if (!dailyHours) return null;

    const days = [
      { name: 'Mon', hours: dailyHours.monday },
      { name: 'Tue', hours: dailyHours.tuesday },
      { name: 'Wed', hours: dailyHours.wednesday },
      { name: 'Thu', hours: dailyHours.thursday },
      { name: 'Fri', hours: dailyHours.friday },
    ];

    return days
      .filter(day => day.hours > 0)
      .map(day => {
        const alertThreshold = isMissingHours 
          ? day.hours - config.threshold 
          : day.hours + config.threshold;
        return `${day.name} (${day.hours}h): alerts at ${alertThreshold.toFixed(1)}h`;
      })
      .join(' | ');
  };

  return (
    <div className={`border rounded-lg p-4 space-y-4 transition-opacity ${!config.enabled ? 'opacity-60' : ''}`}>
      {/* Header Section */}
      <div className="flex items-center gap-3">
        <Checkbox
          checked={config.enabled}
          onCheckedChange={handleToggle}
          id={`policy-${title.toLowerCase().replace(/\s+/g, '-')}`}
        />
        <Label
          htmlFor={`policy-${title.toLowerCase().replace(/\s+/g, '-')}`}
          className="text-base font-semibold cursor-pointer flex-1"
        >
          {title}
        </Label>
        <Badge variant={getSeverityBadgeVariant(config.severity)}>
          {getSeverityLabel(config.severity)}
        </Badge>
      </div>

      {/* Configuration Fields Grid */}
      <div className="grid grid-cols-2 gap-4">
        {/* Row 1, Column 1: Expected Hours Display, Alert When, or Maximum Allowed */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">
            {isLegalMaximum
              ? 'Maximum allowed'
              : (isNonWorkingDay 
                  ? 'Alert when'
                  : (monitoringFrequency === 'week' ? 'Expected hours per week' : 'Expected hours'))}
          </Label>
          {isLegalMaximum ? (
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={thresholdInput}
                  onChange={(e) => handleThresholdChange(e.target.value)}
                  onBlur={handleThresholdBlur}
                  disabled={!config.enabled}
                  step="0.5"
                  min="0"
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">
                  hours per {monitoringFrequency}
                </span>
              </div>
            </div>
          ) : (
            <Input
              type="text"
              value={
                isNonWorkingDay
                  ? 'Time logged on weekends/holidays'
                  : (monitoringFrequency === 'week'
                      ? `${expectedHoursPerWeek}h (from working profile)`
                      : "Uses each day's configured hours")
              }
              disabled
              className="bg-muted/50 cursor-not-allowed"
            />
          )}
        </div>

        {/* Row 1, Column 2: Overtime/Missing Threshold Input or Empty */}
        {!isNonWorkingDay && !isLegalMaximum ? (
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              {isMissingHours
                ? (monitoringFrequency === 'week'
                    ? 'Alert when under by'
                    : "Alert when under each day's expected hours by")
                : (monitoringFrequency === 'week'
                    ? 'Alert when over by'
                    : "Alert when over each day's expected hours by")}
            </Label>
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  value={thresholdInput}
                  onChange={(e) => handleThresholdChange(e.target.value)}
                  onBlur={handleThresholdBlur}
                  disabled={!config.enabled}
                  step="0.5"
                  min="0"
                  className="flex-1"
                />
                <span className="text-sm text-muted-foreground">hour(s)</span>
              </div>
              {monitoringFrequency === 'week' && (
                <p className="text-xs text-muted-foreground">
                  will alert at {weeklyAlertThreshold.toFixed(1)}h
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Empty column for grid alignment */}
          </div>
        )}

        {/* Row 2, Column 1: Severity Selector or Fixed */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Severity</Label>
          {isLegalMaximum ? (
            <Input
              type="text"
              value="Critical (fixed)"
              disabled
              className="bg-muted/50 cursor-not-allowed"
            />
          ) : (
            <Select
              value={config.severity}
              onValueChange={handleSeverityChange}
              disabled={!config.enabled}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="warning">Warning</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Row 2, Column 2: Enforcement Mode or Fixed */}
        <div className="space-y-2">
          <Label className="text-sm text-muted-foreground">Enforcement</Label>
          {isLegalMaximum ? (
            <Input
              type="text"
              value="Block submission (fixed)"
              disabled
              className="bg-muted/50 cursor-not-allowed"
            />
          ) : (
            <Select
              value={config.enforcement}
              onValueChange={handleEnforcementChange}
              disabled={!config.enabled}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="warn">Warn only</SelectItem>
                <SelectItem value="block">Block submission</SelectItem>
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Daily Breakdown Info Box (only for daily monitoring) */}
      {monitoringFrequency === 'day' && dailyHours && (
        <div className="flex gap-2 p-3 bg-info-50 dark:bg-info-950/20 border border-info-200 dark:border-info-800 rounded-lg">
          <Info className="h-4 w-4 text-info-600 dark:text-info-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-info-900 dark:text-info-100">
            <p className="font-medium mb-1">Daily alert thresholds:</p>
            <p className="text-info-700 dark:text-info-300">{getDailyBreakdown()}</p>
          </div>
        </div>
      )}
    </div>
  );
};
